import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { RUTAS } from '@/api/rutas';

import { AccessTime, Email } from '@mui/icons-material';
import { Alert, Box, Button, Container, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';

import { useCaptcha } from '@/components/Captcha';
import { MINIMO_DEL_MENSAJE, faltaParaElMinimo, tieneLink } from '@/lib/contacto';
import { MedirVisita } from '@/components/MedirVisita';

import type { Route } from './+types/contactenos';

export function meta(): Route.MetaDescriptors {
  return [
    { title: 'Contáctenos | CKING Repuestos para Motos' },
    {
      name: 'description',
      content: 'Escribinos a info@cking.com.ar. Atención de lunes a viernes de 8 a 17.',
    },
    { tagName: 'link', rel: 'canonical', href: 'https://cking.com.ar/contactenos' },
  ];
}

export default function Contactenos() {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** La trampa para robots. Una persona no la ve, así que queda vacía. */
  const [trampa, setTrampa] = useState('');

  /**
   * Cuándo se abrió el formulario. Se mide desde acá y no desde la última cuenta del captcha:
   * quien se equivocó en la cuenta contesta la nueva en dos segundos, y no es un robot.
   */
  const abiertoEn = useRef<number | null>(null);

  useEffect(() => {
    abiertoEn.current = Date.now();
  }, []);

  const captcha = useCaptcha();

  const falta = faltaParaElMinimo(mensaje);
  const linkEnElNombre = tieneLink(nombre);
  const linkEnElMensaje = tieneLink(mensaje);

  /**
   * Manda la consulta al servidor, donde queda esperando en el panel.
   *
   * Antes esto abría Gmail con el texto ya escrito, y la nota que había acá decía por qué: un
   * formulario que llega a un servidor es un formulario que un robot puede usar para mandar spam.
   * Eso sigue siendo cierto, así que la consulta va con la misma cuenta a resolver que pide el
   * inicio de sesión, y el servidor además limita cuántas acepta por dirección.
   *
   * Lo que se gana: una consulta escrita a las once de la noche ya no depende de que alguien
   * tuviera Gmail abierto, y queda con fecha y hora en una lista que se puede contestar y
   * archivar.
   */
  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      const respuesta = await fetch(RUTAS.consultas.raiz, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nombre,
          email,
          message: mensaje,
          captchaToken: captcha.token,
          captchaAnswer: captcha.respuesta,
          web: trampa,
          elapsedMs: abiertoEn.current === null ? null : Date.now() - abiertoEn.current,
        }),
      });

      const cuerpo = (await respuesta.json()) as { error?: string };

      if (!respuesta.ok) {
        // Otra cuenta: el token que acaba de usarse ya no sirve, y sin esto el segundo intento
        // fallaría por el captcha y no por lo que de verdad estaba mal.
        captcha.refrescar();
        setError(cuerpo.error ?? 'No se pudo enviar la consulta.');
        return;
      }

      setEnviado(true);
      setNombre('');
      setEmail('');
      setMensaje('');
    } catch {
      captcha.refrescar();
      setError('No se pudo enviar la consulta. Probá de nuevo en un momento.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <MedirVisita path="/contactenos" />

      <Box
        sx={{
          py: 6,
          bgcolor: '#0a0a0a',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            // Pesaba 12 MB. Es un fondo con medio velo encima: 27 KB en el celular alcanzan.
            backgroundImage: 'url(/img/contactenos/contactenos-800.webp)',
            '@media (min-width: 900px)': {
              backgroundImage: 'url(/img/contactenos/contactenos-1600.webp)',
            },
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.5,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              component="h1"
              align="center"
              sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase' }}
            >
              Contáctenos
            </Typography>

            <Typography
              variant="h5"
              component="p"
              align="center"
              sx={{ mb: 4, maxWidth: 800, mx: 'auto', color: 'rgba(255, 255, 255, 0.8)' }}
            >
              Estamos aquí para responder tus preguntas y ayudarte con todo lo que necesites.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* El formulario y los datos de contacto, en la misma fila. Antes eran dos Container
          seguidos, cada uno con py: 8, y entre dos bloques que hablan de lo mismo quedaba un
          vacio de ciento veintiocho pixeles. */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: 7 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Consultas
              </Typography>

              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={evento => void enviar(evento)}
                sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {enviado && (
                  <Alert severity="success" onClose={() => setEnviado(false)}>
                    Recibimos tu consulta. Te vamos a responder por email.
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <TextField
                  label="Nombre"
                  variant="outlined"
                  fullWidth
                  required
                  error={linkEnElNombre}
                  helperText={linkEnElNombre ? 'Sin links en el nombre.' : undefined}
                  value={nombre}
                  onChange={evento => setNombre(evento.target.value)}
                />

                <TextField
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required
                  helperText="A esta dirección te vamos a responder."
                  value={email}
                  onChange={evento => setEmail(evento.target.value)}
                />

                <TextField
                  label="Descripción del mensaje"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  error={linkEnElMensaje}
                  helperText={ayudaDelMensaje(mensaje, falta, linkEnElMensaje)}
                  value={mensaje}
                  onChange={evento => setMensaje(evento.target.value)}
                />

                {/* La trampa para robots: un campo que una persona no ve ni alcanza con el teclado.
                    Corrido fuera de la pantalla y no escondido con display:none, porque hay robots
                    que saltean justamente lo escondido. Si vuelve lleno, lo llenó un robot. */}
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    left: '-10000px',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                  }}
                >
                  <label htmlFor="web">Dejá este campo vacío</label>
                  <input
                    id="web"
                    name="web"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={trampa}
                    onChange={evento => setTrampa(evento.target.value)}
                  />
                </Box>

                {captcha.control}

                {/* Ancho propio, no el del formulario entero: un boton rojo de novecientos
                pixeles de ancho pesa mas que todo lo que tiene arriba. */}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    enviando || !captcha.listo || falta > 0 || linkEnElNombre || linkEnElMensaje
                  }
                  sx={{ alignSelf: 'flex-start', px: 4 }}
                >
                  {enviando ? 'Enviando…' : 'Enviar consulta'}
                </Button>

                {/* La Ley 25.326 (art. 6) pide avisar para qué se usan los datos antes de
                    pedirlos, no en una página aparte que nadie abre. */}
                <Typography variant="body2" sx={{ color: 'grey.500' }}>
                  Usamos tu nombre y tu email sólo para responderte. Más detalle en la{' '}
                  <Box component={Link} to="/privacidad" sx={{ color: 'primary.main' }}>
                    Política de Privacidad
                  </Box>
                  .
                </Typography>
              </Box>
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Información de Contacto
              </Typography>

              {/* En una tarjeta y no sueltos en la pagina: son dos datos cortos, y sin nada que los
              contenga flotaban en el medio de un espacio enorme. */}
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  borderRadius: 2,
                  bgcolor: '#1a1a1a',
                  border: '1px solid rgba(255,255,255,.06)',
                }}
              >
                <Dato icono={<Email fontSize="large" color="primary" />} titulo="Email">
                  info@cking.com.ar
                </Dato>

                <Dato
                  icono={<AccessTime fontSize="large" color="primary" />}
                  titulo="Horario de Atención"
                >
                  Lunes a Viernes: 8:00 - 17:00
                </Dato>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

/**
 * Lo que dice debajo del mensaje mientras se escribe: cuánto falta, o qué sacar.
 *
 * Un espacio cuando no hay nada que decir, y no nada: así el campo no salta un renglón cuando
 * el mensaje llega al mínimo.
 */
function ayudaDelMensaje(mensaje: string, falta: number, conLink: boolean): string {
  if (conLink) return 'Sacá los links: por este formulario no se pueden mandar.';
  if (mensaje.trim().length === 0)
    return `Contanos qué necesitás (mínimo ${MINIMO_DEL_MENSAJE} caracteres).`;
  if (falta > 0) return `Contanos un poco más: faltan ${falta} caracteres.`;
  return ' ';
}

function Dato({
  icono,
  titulo,
  children,
}: {
  icono: React.ReactNode;
  titulo: string;
  children: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icono}

      <Box sx={{ ml: 2 }}>
        <Typography variant="h6" component="p" sx={{ fontWeight: 600, color: '#fff' }}>
          {titulo}
        </Typography>

        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: '5px' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
